import { DEFAULT_LOCALE, numericDateOptions } from "@iec-id/shared";
import { DateFormatter, parseAbsoluteToLocal } from "@internationalized/date";

/**
 * Форматирование дат и времени витрины. Числа, деньги и Баллы форматирует
 * `@iec-id/shared` (`formatPoints`, `formatMoney`, `pointsWorth`) — здесь их нет
 * намеренно, второй копии быть не должно.
 *
 * Локаль всегда приходит аргументом (из `i18n.language`), а не читается из
 * глобала: один и тот же компонент рендерится и в витрине, и в тестах.
 */

const formatters = new Map<string, DateFormatter>();

function dateFormatter(locale: string, options: Intl.DateTimeFormatOptions): DateFormatter {
	const key = `${locale}:${JSON.stringify(options)}`;
	let cached = formatters.get(key);
	if (!cached) {
		cached = new DateFormatter(locale, options);
		formatters.set(key, cached);
	}
	return cached;
}

/** ISO-строка → Date. Невалидный вход отдаёт `null`, а не бросает. */
function toDate(iso: string | null | undefined): Date | null {
	if (!iso) return null;
	try {
		return parseAbsoluteToLocal(iso).toDate();
	} catch {
		const fallback = new Date(iso);
		return Number.isNaN(fallback.getTime()) ? null : fallback;
	}
}

/** `23.02.2026` (ru) / `Feb 23, 2026` (en). Набор полей — `numericDateOptions`. */
export function formatDate(iso: string | null | undefined, locale = DEFAULT_LOCALE): string {
	const date = toDate(iso);
	if (!date) return "";
	return dateFormatter(locale, numericDateOptions(locale)).format(date);
}

/** «14 ноября» / «14 November» — дата сгорания в карточке Баллов. */
export function formatDayMonth(iso: string | null | undefined, locale = DEFAULT_LOCALE): string {
	const date = toDate(iso);
	if (!date) return "";
	// Год добавляется, только когда он не текущий. Иначе «сгорят 15 августа»
	// про дату следующего года читается как «через неделю» — а это уже не
	// стилистика, а неверно понятый срок.
	const sameYear = date.getFullYear() === new Date().getFullYear();
	return dateFormatter(
		locale,
		sameYear
			? { month: "long", day: "numeric" }
			: { year: "numeric", month: "long", day: "numeric" },
	).format(date);
}

/** «1 сентября 2026» / «1 September 2026» — дата принятия документа. */
export function formatDateLong(iso: string | null | undefined, locale = DEFAULT_LOCALE): string {
	const date = toDate(iso);
	if (!date) return "";
	return dateFormatter(locale, { year: "numeric", month: "long", day: "numeric" }).format(date);
}

/** Дата и время — журнал событий и лента уведомлений. */
export function formatDateTime(iso: string | null | undefined, locale = DEFAULT_LOCALE): string {
	const date = toDate(iso);
	if (!date) return "";
	return dateFormatter(locale, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(date);
}

/** Год регистрации для чипа «С IEC с 2024». */
export function yearOf(iso: string | null | undefined): number | null {
	const date = toDate(iso);
	return date ? date.getFullYear() : null;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * «5 минут назад» / «5 minutes ago». Старше недели — абсолютная дата:
 * «37 дней назад» человек всё равно переводит в календарь сам.
 */
export function formatRelativeTime(
	iso: string | null | undefined,
	locale = DEFAULT_LOCALE,
	now: Date = new Date(),
): string {
	const date = toDate(iso);
	if (!date) return "";
	const diff = date.getTime() - now.getTime();
	const absolute = Math.abs(diff);
	if (absolute > 7 * DAY) return formatDate(iso, locale);

	const relative = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
	if (absolute < HOUR) return relative.format(Math.round(diff / MINUTE), "minute");
	if (absolute < DAY) return relative.format(Math.round(diff / HOUR), "hour");
	return relative.format(Math.round(diff / DAY), "day");
}

/** Целых дней между двумя моментами; отрицательное значение — дата в прошлом. */
export function daysUntil(iso: string | null | undefined, now: Date = new Date()): number | null {
	const date = toDate(iso);
	if (!date) return null;
	return Math.ceil((date.getTime() - now.getTime()) / DAY);
}

/**
 * Доля пройденного отрезка `[start, end]` в процентах, 0–100.
 * Используется и полосой сгорания Баллов, и полосой использованных дней IEC+.
 */
export function elapsedPercent(
	startIso: string | null | undefined,
	endIso: string | null | undefined,
	now: Date = new Date(),
): number {
	const start = toDate(startIso);
	const end = toDate(endIso);
	if (!start || !end) return 0;
	const total = end.getTime() - start.getTime();
	if (total <= 0) return 100;
	const passed = now.getTime() - start.getTime();
	return Math.max(0, Math.min(100, Math.round((passed / total) * 100)));
}

/** Сколько дней отрезка уже прошло и сколько в нём всего — подпись под полосой. */
export function elapsedDays(
	startIso: string | null | undefined,
	endIso: string | null | undefined,
	now: Date = new Date(),
): { used: number; total: number } | null {
	const start = toDate(startIso);
	const end = toDate(endIso);
	if (!start || !end) return null;
	const total = Math.max(1, Math.round((end.getTime() - start.getTime()) / DAY));
	const used = Math.max(0, Math.min(total, Math.round((now.getTime() - start.getTime()) / DAY)));
	return { used, total };
}
